import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, Plus, ShieldCheck, Sparkles, Trash2 } from 'lucide-react';
import { engagement } from '../../../lib/api';
import { useLocale } from '../../../i18n/LocaleContext';

const EMPTY_FORM = { authorName: '', postUrl: '', content: '' };

function ManualPostCard({ post, onDelete, deleting, t }) {
  return (
    <article className="rounded-2xl border border-[#D7DCE4] bg-white p-4 shadow-[0_18px_40px_-34px_rgba(11,29,58,.4)] sm:p-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-[#0B1D3A]">{post.author_name || t('Unknown author')}</p>
          <p className="mt-1 text-xs text-[#667085]">{post.approval_status ? t(post.approval_status) : t('No draft yet')}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          {post.post_url && <a href={post.post_url} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-[#667085] hover:bg-[#F2F4F7]" aria-label={t('Open on LinkedIn')}><ExternalLink className="h-4 w-4" /></a>}
          <button type="button" onClick={() => onDelete(post.id)} disabled={deleting} className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50" aria-label={t('Delete manual post')}><Trash2 className="h-4 w-4" /></button>
        </div>
      </header>
      <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-6 text-[#344054]">{post.content}</p>
    </article>
  );
}

export default function EngagementWorkflow() {
  const { locale, intlLocale, t } = useLocale();
  const [posts, setPosts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');

  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const [postResult, jobResult] = await Promise.all([
        engagement.listManualPosts(),
        engagement.listJobs(),
      ]);
      setPosts(postResult.items || []);
      setJobs((jobResult.items || []).filter((job) => job.kind === 'concepts'));
      setError('');
    } catch (err) {
      setError(err.message || t('Could not load Engagement data.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!jobs.some((job) => ['queued', 'processing'].includes(job.status))) return undefined;
    const timer = window.setInterval(() => load({ quiet: true }), 2000);
    return () => window.clearInterval(timer);
  }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPost = async (event) => {
    event.preventDefault();
    if (!form.content.trim()) return;
    setAction('add'); setError('');
    try {
      await engagement.createManualPost({
        content: form.content.trim(),
        authorName: form.authorName.trim() || null,
        postUrl: form.postUrl.trim() || null,
      });
      setForm(EMPTY_FORM);
      await load({ quiet: true });
    } catch (err) {
      setError(err.message || t('Could not add the post.'));
    } finally { setAction(''); }
  };

  const deletePost = async (postId) => {
    setAction(`delete-${postId}`); setError('');
    try { await engagement.deleteManualPost(postId); await load({ quiet: true }); }
    catch (err) { setError(err.message || t('Could not delete the post.')); }
    finally { setAction(''); }
  };

  const generateDrafts = async () => {
    setAction('concepts'); setError('');
    try { await engagement.buildConcepts({ locale }); await load({ quiet: true }); }
    catch (err) { setError(err.message || t('Could not build the concepts. Try again later.')); }
    finally { setAction(''); }
  };

  return (
    <div className="app-page-shell bg-[#F7F8F6]">
      <header className="shrink-0 border-b border-[#D7DCE4] bg-[#F7F8F6] px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div><h1 className="text-xl font-bold text-[#0B1D3A]">{t('Engagement')}</h1><p className="text-xs text-[#667085]">{t('Create human-reviewed comment drafts without automated LinkedIn access.')}</p></div>
          <button type="button" onClick={generateDrafts} disabled={Boolean(action) || posts.length === 0} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#0B1D3A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Sparkles className="h-4 w-4" />{t(action === 'concepts' ? 'Generating drafts...' : 'Generate drafts')}</button>
        </div>
      </header>

      <div className="app-page-scroll">
        <main className="mx-auto max-w-5xl space-y-5 px-4 py-6 pb-28 sm:px-6">
          <div className="flex gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <div><p className="font-bold">{t('Manual safety mode')}</p><p className="mt-1 leading-6">{t('Linirio does not scan profiles, fetch posts, read comments, or publish Engagement comments. Paste the post here, review the AI draft, and publish it yourself on LinkedIn.')}</p></div>
          </div>
          {error && <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"><AlertCircle className="h-5 w-5 shrink-0" /><span>{error}</span></div>}

          <form onSubmit={addPost} className="rounded-2xl border border-[#D7DCE4] bg-white p-4 sm:p-5">
            <div className="flex items-center gap-2"><Plus className="h-5 w-5 text-[#12A9A8]" /><h2 className="font-bold text-[#0B1D3A]">{t('Add a post manually')}</h2></div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium text-[#344054]">{t('Author name')}<input value={form.authorName} onChange={(e) => setForm({ ...form, authorName: e.target.value })} maxLength={200} className="mt-1.5 h-11 w-full rounded-xl border border-[#C9D0DA] px-3 outline-none focus:border-[#12C8C6]" placeholder={t('Optional')} /></label>
              <label className="text-sm font-medium text-[#344054]">{t('LinkedIn post URL')}<input type="url" value={form.postUrl} onChange={(e) => setForm({ ...form, postUrl: e.target.value })} maxLength={2048} className="mt-1.5 h-11 w-full rounded-xl border border-[#C9D0DA] px-3 outline-none focus:border-[#12C8C6]" placeholder="https://www.linkedin.com/posts/..." /></label>
            </div>
            <label className="mt-3 block text-sm font-medium text-[#344054]">{t('Post text')}<textarea required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} maxLength={12000} rows={7} className="mt-1.5 w-full resize-y rounded-xl border border-[#C9D0DA] p-3 leading-6 outline-none focus:border-[#12C8C6]" placeholder={t('Paste the complete post text here.')} /></label>
            <div className="mt-3 flex items-center justify-between gap-3"><p className="text-xs text-[#667085]">{t('The URL is stored only. Linirio never opens or downloads it.')}</p><button type="submit" disabled={Boolean(action) || !form.content.trim()} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl bg-[#12C8C6] px-4 text-sm font-bold text-white disabled:opacity-50">{action === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}{t('Add post')}</button></div>
          </form>

          <section>
            <div className="mb-3 flex items-center justify-between"><h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[#667085]">{t('Manual posts')}</h2><span className="text-xs text-[#667085]">{posts.length}</span></div>
            {loading ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#12C8C6]" /></div> : posts.length ? <div className="space-y-3">{posts.map((post) => <ManualPostCard key={post.id} post={post} onDelete={deletePost} deleting={action === `delete-${post.id}`} t={t} />)}</div> : <div className="rounded-2xl border border-dashed border-[#C9D0DA] bg-white px-5 py-16 text-center"><CheckCircle2 className="mx-auto h-9 w-9 text-[#12C8C6]" /><h2 className="mt-4 font-bold text-[#0B1D3A]">{t('No manual posts yet')}</h2><p className="mt-2 text-sm text-[#667085]">{t('Paste a LinkedIn post above to create a reviewed comment draft.')}</p></div>}
          </section>

          {jobs.length > 0 && <section><h2 className="mb-3 text-sm font-bold uppercase tracking-[0.12em] text-[#667085]">{t('Concept generation')}</h2><div className="space-y-2">{jobs.slice(0, 5).map((job) => <div key={job.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#D7DCE4] bg-white p-3"><div><p className="text-sm font-semibold text-[#0B1D3A]">{t(job.status)}</p><p className="text-xs text-[#667085]">{job.created_at ? new Date(job.created_at).toLocaleString(intlLocale) : ''}</p></div><span className="text-xs text-[#667085]">{job.result?.created || job.progress || 0} / {job.total || posts.length}</span></div>)}</div></section>}
        </main>
      </div>
    </div>
  );
}
